import { useContext, useMemo } from 'react'
import AppContext from '../../context/appContext'
import {
  useCreateDocument,
  useDocuments,
  useRemoveDocuments,
  useUpdateDocument,
} from '../../hooks/data/useDocuments'
import { useFormDefinition } from '../../hooks/data/useFormDefinitions'
import { useProcessLoading } from '../../utils/Loading/useProcessLoading'
import { FormDefinitionDialog } from './FormDefinitionDialog'

const farmSubdivisionEntityTypeId = 0

const FarmSubdivisionDocumentsDialog = ({
  readOnly,
  farmSubdivisionId,
  year,
  open,
  handleClose,
}) => {
  const { processLoading } = useProcessLoading()
  const { programConfig, partialChanges, setPartialChanges, setConfirmationModalConfig } =
    useContext(AppContext)

  const formDefinitionId = open
    ? programConfig?.formDefinitions?.farmManagementSupportiveDocumentation?.id
    : undefined
  const { data: formDefinition } = useFormDefinition(formDefinitionId)

  const fieldInputsFromFormDefinition = useMemo(
    () => formDefinition?.fields?.filter(field => field.component_type === 'FileInput') ?? null,
    [formDefinition],
  )
  const documentsTypes = useMemo(
    () => fieldInputsFromFormDefinition?.map(field => field.fileType) ?? null,
    [fieldInputsFromFormDefinition],
  )

  // The document types are only known once the form definition resolves; gating on them keeps the
  // query from firing an unfiltered first request that a second one would immediately supersede.
  const { data: documentsFromEntity } = useDocuments({
    entityId: open && documentsTypes ? farmSubdivisionId : undefined,
    entityTypeId: farmSubdivisionEntityTypeId,
    documentsTypes,
  })

  const { mutateAsync: createDocuments } = useCreateDocument()
  const { mutateAsync: updateDocuments } = useUpdateDocument()
  const { mutateAsync: removeDocuments } = useRemoveDocuments()

  const formData = useMemo(() => {
    if (!fieldInputsFromFormDefinition || !documentsFromEntity) return null

    return fieldInputsFromFormDefinition.reduce(
      (formDataFromDocumentsEntity, fieldInputFromFormDefinition) => ({
        ...formDataFromDocumentsEntity,
        [fieldInputFromFormDefinition.name]: documentsFromEntity
          .filter(
            documentFromEntity =>
              fieldInputFromFormDefinition.fileType === documentFromEntity.documentTypeId,
          )
          .map(documentFromEntity => documentFromEntity.url),
      }),
      {},
    )
  }, [fieldInputsFromFormDefinition, documentsFromEntity])

  const handlePartialChangesClose = () => {
    handleClose()
    setPartialChanges(false)
  }

  const handleSaveFormData = async data => {
    await processLoading({
      loadingMessage: 'Guardando documentos...',
      successfulMessage: 'Documentos guardados',
      doAction: async () => {
        const documentsFromFileInput = Object.keys(data).flatMap(fileInputType => {
          const fileInputURLByType = data[fileInputType]
          if (!fileInputURLByType || !fileInputURLByType.length) return []

          const fileInputFileByName = fieldInputsFromFormDefinition.find(
            fieldInputFromFormDefinition => fieldInputFromFormDefinition.name === fileInputType,
          )
          return fileInputURLByType.map(documentURL => ({
            documentTypeId: fileInputFileByName.fileType,
            entityTypeId: farmSubdivisionEntityTypeId,
            entityId: farmSubdivisionId,
            url: documentURL,
          }))
        })

        const documentsToUpdate = []
        const documentsToCreate = []
        documentsFromFileInput.forEach(documentFromFileInput => {
          const documentOriginalLoaded = documentsFromEntity.find(documentFromEntity => {
            return documentFromEntity.url === documentFromFileInput.url
          })
          if (!documentOriginalLoaded) {
            documentsToCreate.push(documentFromFileInput)
          } else {
            documentsToUpdate.push({ ...documentOriginalLoaded })
          }
        })

        const documentsToDelete = documentsFromEntity.filter(documentFromEntity => {
          return documentsFromFileInput.every(documentFromFileInput => {
            return documentFromEntity.url !== documentFromFileInput.url
          })
        })

        if (documentsToCreate.length) await createDocuments({ document: documentsToCreate })
        if (documentsToUpdate.length) await updateDocuments({ document: documentsToUpdate })
        if (documentsToDelete.length) await removeDocuments({ documents: documentsToDelete })

        handlePartialChangesClose()
      },
    })
  }

  const handleCloseAndReset = () => {
    if (partialChanges) {
      setConfirmationModalConfig({
        open: true,
        confirmAction: () => {
          handlePartialChangesClose()
        },
      })
    } else {
      handleClose()
    }
  }

  return (
    formDefinition &&
    formDefinition.fields &&
    formDefinition.fields.length > 0 &&
    formData &&
    documentsFromEntity &&
    fieldInputsFromFormDefinition && (
      <FormDefinitionDialog
        data={formData}
        formDefinition={formDefinition}
        handleClose={handleCloseAndReset}
        open={open}
        readOnly={readOnly}
        saveFormData={handleSaveFormData}
        title={`Año ${year}`}
        year={year}
      />
    )
  )
}

export default FarmSubdivisionDocumentsDialog
